import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, Key, Shield, Lock, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const PassphraseRecoveryGuide = () => {
  return (
    <div className="App">
      <Helmet>
        <title>BIP39 Passphrase (25th Word) Recovery Guide | ARCANA CRYPTO</title>
        <meta name="description" content="Forgotten your BIP39 passphrase (25th word)? Learn safe recovery methods, pattern analysis, and when dictionary-based recovery works. Non-custodial. Free assessment." />
        <meta name="keywords" content="BIP39 passphrase, 25th word recovery, passphrase forgotten, BIP39 extension word, seed phrase passphrase, passphrase recovery" />
        <link rel="canonical" href="https://arcana-crypto.com/passphrase-25th-word-recovery.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
              BIP39 Passphrase (25th Word) Recovery: Safe Methods & Feasibility
            </h1>
            <p className="section-intro" style={{fontSize: '18px', marginBottom: '40px'}}>
              The <strong>BIP39 passphrase</strong> (also called "25th word" or "extension word") adds an extra 
              layer of security—but losing it means your funds appear gone even with the correct seed phrase. 
              Recovery is possible when you have <strong>partial memory or patterns</strong>.
            </p>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Passphrase is <strong>optional</strong> but if used, it's <strong>mandatory</strong> for access.</li>
              <li>Even a <strong>single character wrong</strong> creates a completely different wallet.</li>
              <li>Recovery depends on <strong>patterns, hints, and memory clues</strong>.</li>
              <li>Dictionary-based recovery works when passphrase uses real words.</li>
              <li>Non-custodial recovery is possible with proper methodology.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Write down <strong>everything you remember</strong> (words, phrases, numbers, patterns).</li>
                <li>Note <strong>character types used</strong> (lowercase, uppercase, numbers, symbols).</li>
                <li>Estimate <strong>length range</strong> (was it short phrase or long sentence?).</li>
                <li>Check for <strong>common patterns</strong> (names, dates, favorite quotes, song lyrics).</li>
                <li>Identify wallet software and any <strong>known receiving addresses</strong>.</li>
                <li>Work offline and use <strong>encrypted channels</strong> for sensitive discussion.</li>
              </ul>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not try <strong>random passphrases</strong> without methodology.</li>
                <li>Avoid web-based "passphrase checkers" or online tools.</li>
                <li>Do not assume "blank passphrase" if you're unsure.</li>
                <li>Do not share your <strong>seed phrase + passphrase attempts</strong> together.</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Lock size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                The <strong>BIP39 passphrase</strong> (extension word) is combined with the seed phrase to derive 
                completely different private keys. Even one character difference creates an entirely separate wallet 
                with its own addresses and balances.
              </p>
              <p style={{marginTop: '16px'}}>
                <strong>Recovery approaches:</strong>
              </p>
              <ul style={{marginTop: '12px', marginLeft: '20px'}}>
                <li><strong>Dictionary-based:</strong> Testing real words, common phrases, song lyrics, quotes</li>
                <li><strong>Pattern-based:</strong> Using known patterns (names+dates, favorite words+numbers)</li>
                <li><strong>Rule-based:</strong> Character substitutions, capitalization variants, number additions</li>
                <li><strong>Hybrid:</strong> Combining multiple approaches based on memory clues</li>
              </ul>
              <p style={{marginTop: '16px'}}>
                Feasibility depends heavily on <strong>entropy</strong> (randomness). Short passphrases with patterns 
                (e.g., "MyDog2019!") are more recoverable than long random strings. Having a known receiving address 
                allows validation of passphrase candidates.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Strictly <strong>non-custodial</strong>: we never access your funds</li>
                <li>Seed phrase + passphrase handled securely if required</li>
                <li>Offline processing with encrypted communications</li>
                <li>Passphrase candidates tested systematically without exposure</li>
                <li>Successful passphrase delivered encrypted - you access wallet</li>
                <li>Data deletion after completion (GDPR compliant)</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="two-column">
              <div>
                <h3 className="column-title" style={{fontSize: '20px', color: 'var(--oro-arcana)'}}>
                  MORE LIKELY ✓
                </h3>
                <p className="legal-content">
                  Passphrase uses real words, common phrases, or known patterns. You remember partial 
                  information (base words, approximate length, character types). You have a known address for validation.
                </p>
              </div>
              <div>
                <h3 className="column-title" style={{fontSize: '20px', color: 'var(--oro-arcana)'}}>
                  LESS LIKELY ✗
                </h3>
                <p className="legal-content">
                  Passphrase is completely random, very long (20+ chars), no patterns, and no memory clues. 
                  Computational requirements may be extreme relative to wallet value.
                </p>
              </div>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Network (BTC/ETH/LTC/DOGE) and wallet software</li>
                <li>Passphrase memory clues (base words, patterns, length estimate)</li>
                <li>Character types used (words, numbers, symbols, case)</li>
                <li>Any known receiving address or xpub (critical for validation)</li>
                <li>Context (why you chose that passphrase, when created)</li>
                <li>Time sensitivity</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="What's the difference between seed phrase and passphrase?"
                answer="Seed phrase (12-24 words) is mandatory. Passphrase (25th word) is optional extra security. If you set a passphrase, it becomes mandatory for access."
              />
              <FAQItem 
                question="Can I just try blank/empty passphrase?"
                answer="Only if you're certain no passphrase was set. Trying empty passphrase on a passphrase-protected wallet creates a different (empty) wallet."
              />
              <FAQItem 
                question="Do you need my seed phrase?"
                answer="Not via form/chat. For passphrase recovery, seed+passphrase handling is discussed securely if required for validation."
              />
              <FAQItem 
                question="How long does passphrase recovery take?"
                answer="Dictionary-based (common words): hours to days. Pattern-based (with strong clues): hours to days. Random/long passphrases: days to weeks or not feasible."
              />
              <FAQItem 
                question="What affects feasibility?"
                answer="Passphrase entropy (randomness), length, your memory clues, and whether you have address hints for validation."
              />
              <FAQItem 
                question="Is it cheaper than seed phrase recovery?"
                answer="Depends on complexity. Simple passphrase (1-2 words): £600. Complex pattern-based: £1,000. Extreme cases: higher."
              />
              <FAQItem 
                question="Can you recover if I remember nothing?"
                answer="Very difficult. Assessment determines if any contextual clues (wallet creation date, amounts, usage patterns) can narrow search space."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Forgotten Your BIP39 Passphrase?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Free confidential assessment - we evaluate feasibility first
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              FREE PASSPHRASE RECOVERY ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Non-custodial • Pattern analysis • Dictionary methods • No guarantees
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Guides:</strong>
              </p>
              <a href="/resources/seed-phrase-missing-1-word" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                Seed Missing 1 Word →
              </a>
              <a href="/resources/seed-phrase-typos-wrong-order" className="card-link" style={{fontSize: '16px'}}>
                Typos/Wrong Order →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PassphraseRecoveryGuide;
